import type {
  STChecklistItem,
  Jurisdiction,
  GetPaymentInstructions,
  BulkCreateChecklistItemsItem
} from '@st/sdk'
import { match } from '@st/pattern'

export type PaymentInstructionsRequest = GetPaymentInstructions & { method: 'online' | 'mail' }

export type ChecklistDraft = Pick<STChecklistItem, 'dueOn'> & {
  note: string
}
type Context<K extends STChecklistItem['context']['type']> = Extract<
  STChecklistItem['context'],
  { type: K }
>
export type ChecklistTarget =
  | { mode: 'create'; phase: STChecklistItem['phase'] }
  | { mode: 'edit'; item: STChecklistItem }
type Target<K extends STChecklistItem['context']['type']> =
  | Extract<ChecklistTarget, { mode: 'create' }>
  | { mode: 'edit'; item: Omit<STChecklistItem, 'context'> & { context: Context<K> } }
type Draft<K extends STChecklistItem['context']['type']> = ChecklistDraft & { context: Context<K> }
export type ChecklistEditor =
  | {
      kind: 'document_request'
      target: Target<'document_request'>
      draft: Draft<'document_request'> & { name: string; incompleteReason: string }
    }
  | {
      kind: 'questionnaire_entry'
      target: Extract<Target<'questionnaire_entry'>, { mode: 'edit' }>
      draft: Draft<'questionnaire_entry'> & { incompleteReason: string }
    }
  | {
      kind: 'tax_payment'
      target: Target<'tax_payment'>
      draft: Draft<'tax_payment'>
      instructions?:
        | { status: 'failed' }
        | { status: 'loading'; request: PaymentInstructionsRequest }
      catalog: Jurisdiction[]
    }
type PaymentEditor = Extract<ChecklistEditor, { kind: 'tax_payment' }>
export type ChecklistDialog =
  | {
      mode: 'generation'
      status: 'loading' | 'ready' | 'saving' | 'failed'
      items: BulkCreateChecklistItemsItem[]
      error?: string
    }
  | { mode: 'edit'; editor: ChecklistEditor }
  | { mode: 'loading'; editor: Omit<PaymentEditor, 'catalog'> }
  | {
      mode: 'attachments' | 'complete'
      item: STChecklistItem
      attachments: STChecklistItem['attachments']
      isAttachmentPickerOpen: boolean
    }
  | { mode: 'delete' | 'manageCompletion'; item: STChecklistItem }
export type ChecklistDialogRequest =
  | ChecklistTarget
  | { mode: 'attachments' | 'delete' | 'manageCompletion'; item: STChecklistItem }

export function initialChecklistDialogState(
  request: ChecklistDialogRequest,
  year: number,
  catalog: Jurisdiction[] | undefined,
  returnType: string
): ChecklistDialog {
  function payment(editor: Omit<PaymentEditor, 'catalog'>): ChecklistDialog {
    return match(catalog)
      .with(undefined, () => ({ mode: 'loading' as const, editor }))
      .otherwise((catalog) => ({ mode: 'edit' as const, editor: { ...editor, catalog } }))
  }
  return match(request)
    .with({ mode: 'create' }, (target) =>
      match(target.phase)
        .with('intake', () => ({
          mode: 'edit' as const,
          editor: {
            kind: 'document_request' as const,
            target,
            draft: {
              name: '',
              note: '',
              incompleteReason: '',
              context: { type: 'document_request' as const, documentTypeId: '', match: {} }
            }
          }
        }))
        .with('delivery', () => {
          const context: Context<'tax_payment'> = {
            type: 'tax_payment',
            paymentPurpose: 'balance_due',
            jurisdiction: { level: 'federal', code: 'US', name: 'Federal' },
            amount: '0',
            taxYear: year,
            method: 'online'
          }
          return payment({
            kind: 'tax_payment',
            target,
            draft: { note: '', context },
            instructions: paymentInstructions(context, returnType)
          })
        })
        .exhaustive()
    )
    .with({ mode: 'edit' }, (target) => {
      const { note, dueOn, incompleteReason, context } = target.item
      const draft: ChecklistDraft = {
        note: note ?? '',
        dueOn
      }
      return match(context)
        .with({ type: 'document_request' }, (context) => ({
          mode: 'edit' as const,
          editor: {
            kind: 'document_request' as const,
            target: { ...target, item: { ...target.item, context } },
            draft: {
              ...draft,
              context,
              name: target.item.name,
              incompleteReason: incompleteReason ?? ''
            }
          }
        }))
        .with({ type: 'questionnaire_entry' }, (context) => ({
          mode: 'edit' as const,
          editor: {
            kind: 'questionnaire_entry' as const,
            target: { ...target, item: { ...target.item, context } },
            draft: { ...draft, context, incompleteReason: incompleteReason ?? '' }
          }
        }))
        .with({ type: 'tax_payment' }, (context) =>
          payment({
            kind: 'tax_payment',
            target: { ...target, item: { ...target.item, context } },
            draft: { ...draft, context }
          })
        )
        .exhaustive()
    })
    .with({ mode: 'attachments' }, (request) => ({
      ...request,
      attachments: request.item.attachments,
      isAttachmentPickerOpen: false
    }))
    .with({ mode: 'manageCompletion' }, ({ item }) =>
      match(item.context.type)
        .with('questionnaire_entry', () => ({ mode: 'manageCompletion' as const, item }))
        .otherwise(() => ({
          mode: 'complete' as const,
          item,
          attachments: item.attachments,
          isAttachmentPickerOpen: false
        }))
    )
    .with({ mode: 'delete' }, (request) => request)
    .exhaustive()
}

export function formatChecklistItem(item: STChecklistItem) {
  return [item.name, item.note].filter(Boolean).join(' ')
}

export function paymentInstructions(
  context: Context<'tax_payment'>,
  returnType: string
): PaymentEditor['instructions'] {
  if (!context.jurisdiction.code || context.method == 'automatic_withdrawal') {
    return undefined
  }
  return {
    status: 'loading',
    request: {
      type: 'taxdb/getPaymentInstructions',
      jurisdiction: context.jurisdiction.code,
      taxYear: context.taxYear,
      returnType,
      paymentPurpose: context.paymentPurpose,
      method: context.method
    }
  }
}
